interface Byte {
  hex: string
  dec: number
}

interface Word extends Byte {}

interface Resource {
  current: Word
  max: Word
}

interface Resources {
  energy: Resource
  reserve: Resource
  missiles: Resource
  superMissiles: Resource
  powerBombs: Resource
}

interface CollectionItems {
  crateria: {
    missilePacks: boolean[]
    superMissilePacks: boolean[]
    powerBombPacks: boolean[]
    energyTanks: boolean[]
  }
  brinstar: {
    missilePacks: boolean[]
    superMissilePacks: boolean[]
    powerBombPacks: boolean[]
    energyTanks: boolean[]
    reserveTank: boolean
  }
  norfair: {
    missilePacks: boolean[]
    superMissilePacks: boolean[]
    powerBombPacks: boolean[]
    energyTanks: boolean[]
    reserveTank: boolean
  }
  wreckedShip: {
    missilePacks: boolean[]
    superMissilePacks: boolean[]
    energyTanks: boolean[]
    reserveTank: boolean
  }
  maridia: {
    missilePacks: boolean[]
    superMissilePacks: boolean[]
    powerBombPacks: boolean[]
    energyTanks: boolean[]
    reserveTank: boolean
  }
}

interface Checksum {
  high: Byte
  low: Byte
}

interface Equipment {
  collected: boolean
  inInventory: boolean
  equipped: boolean
}

interface Equipments {
  morphBall: Equipment
  bombs: Equipment
  speedBooster: Equipment
  highJumpBoots: Equipment
  suits: {
    varia: Equipment
    gravity: Equipment
  }
  springBall: Equipment
  spaceJump: Equipment
  screwAttack: Equipment
  beams: {
    charge: Equipment
    ice: Equipment
    wave: Equipment
    spazer: Equipment
    plasma: Equipment
  }
  grapple: Equipment
  xRay: Equipment
}

interface Bosses {
  silverTorizo: boolean
  sporeSpawn: boolean
  kraid: boolean
  phantoon: boolean
  botwoon: boolean
  draygon: boolean
  crocomire: boolean
  goldenTorizo: boolean
  ridley: boolean
}

interface Statues {
  kraid: boolean
  phantoon: boolean
  draygon: boolean
  ridley: boolean
}

export interface Game {
  equipment: Equipments
  resources: Resources
  collectionItems: CollectionItems
  bosses: Bosses
  statues: Statues
  maps: {
    crateria: boolean
    brinstar: boolean
    norfair: boolean
    wreckedShip: boolean
    maridia: boolean
    tourian: boolean
  }
  time: {
    hours: Word
    minutes: Word
  }
  checksums: {
    checksum: Checksum
    complement: Checksum
    checksumCopy: Checksum
    complementCopy: Checksum
  }
}

const offsets = {
  gameData: { a: 0x10, b: 0x66c, c: 0xcc8 },
  checksums: { a: 0x0, b: 0x2, c: 0x4 }
}

export class Srm {
  private srm: Buffer
  public games: { a: Game; b: Game; c: Game }
  constructor(srm: Buffer) {
    this.srm = srm

    this.games = {
      a: this.getGame('a'),
      b: this.getGame('b'),
      c: this.getGame('c')
    }
  }

  serialize(games: { a: Game; b: Game; c: Game }): Uint8Array {
    const buf = new Uint8Array(this.srm)

    for (const key of ['a', 'b', 'c'] as const) {
      const game = games[key]
      const gameOffset = offsets.gameData[key]
      Srm.writeEquipment(buf, gameOffset, game.equipment)
      Srm.writeResources(buf, gameOffset, game.resources)
      Srm.writeCollectionItems(buf, gameOffset, game.collectionItems)
      Srm.writeBosses(buf, gameOffset, game.bosses)
      Srm.writeStatues(buf, gameOffset, game.statues)
      Srm.writeTime(buf, gameOffset, game.time)
      Srm.writeMaps(buf, gameOffset, game.maps)
    }

    for (const key of ['a', 'b', 'c'] as const) {
      const { checksum, complement } = Srm.calcChecksums(
        buf,
        offsets.gameData[key]
      )
      const cOff = offsets.checksums[key]
      buf[cOff] = checksum.high
      buf[cOff + 1] = checksum.low
      buf[cOff + 8] = complement.high
      buf[cOff + 9] = complement.low
      buf[0x1ff0 + cOff] = checksum.high
      buf[0x1ff1 + cOff] = checksum.low
      buf[0x1ff8 + cOff] = complement.high
      buf[0x1ff9 + cOff] = complement.low
    }

    // Hard-coded tail (last 4 bytes)
    buf[0x1ffc] = 0x60
    buf[0x1ffd] = 0x60
    buf[0x1ffe] = 0x60
    buf[0x1fff] = 0x60

    return new Uint8Array(buf)
  }

  private static setBit(
    buf: Uint8Array,
    offset: number,
    bitPosition: number,
    value: boolean
  ): void {
    if (value) buf[offset] |= 1 << bitPosition
    else buf[offset] &= ~(1 << bitPosition)
  }

  private static setWord(buf: Uint8Array, offset: number, value: number): void {
    buf[offset] = value & 0xff
    buf[offset + 1] = (value >> 8) & 0xff
  }

  private static setByte(buf: Uint8Array, offset: number, value: number): void {
    buf[offset] = value & 0xff
  }

  private static writeEquipment(
    buf: Uint8Array,
    offset: number,
    eq: Equipments
  ): void {
    Srm.setBit(buf, 0xb3 + offset, 2, eq.morphBall.collected)
    Srm.setBit(buf, 0x2 + offset, 2, eq.morphBall.inInventory)
    Srm.setBit(buf, 0x0 + offset, 2, eq.morphBall.equipped)

    Srm.setBit(buf, 0xb0 + offset, 7, eq.bombs.collected)
    Srm.setBit(buf, 0x3 + offset, 4, eq.bombs.inInventory)
    Srm.setBit(buf, 0x1 + offset, 4, eq.bombs.equipped)

    Srm.setBit(buf, 0xc2 + offset, 6, eq.springBall.collected)
    Srm.setBit(buf, 0x2 + offset, 1, eq.springBall.inInventory)
    Srm.setBit(buf, 0x0 + offset, 1, eq.springBall.equipped)

    Srm.setBit(buf, 0xb6 + offset, 5, eq.highJumpBoots.collected)
    Srm.setBit(buf, 0x3 + offset, 0, eq.highJumpBoots.inInventory)
    Srm.setBit(buf, 0x1 + offset, 0, eq.highJumpBoots.equipped)

    Srm.setBit(buf, 0xb6 + offset, 0, eq.suits.varia.collected)
    Srm.setBit(buf, 0x2 + offset, 0, eq.suits.varia.inInventory)
    Srm.setBit(buf, 0x0 + offset, 0, eq.suits.varia.equipped)

    Srm.setBit(buf, 0xc0 + offset, 7, eq.suits.gravity.collected)
    Srm.setBit(buf, 0x2 + offset, 5, eq.suits.gravity.inInventory)
    Srm.setBit(buf, 0x0 + offset, 5, eq.suits.gravity.equipped)

    Srm.setBit(buf, 0xb8 + offset, 2, eq.speedBooster.collected)
    Srm.setBit(buf, 0x3 + offset, 5, eq.speedBooster.inInventory)
    Srm.setBit(buf, 0x1 + offset, 5, eq.speedBooster.equipped)

    Srm.setBit(buf, 0xc3 + offset, 2, eq.spaceJump.collected)
    Srm.setBit(buf, 0x3 + offset, 1, eq.spaceJump.inInventory)
    Srm.setBit(buf, 0x1 + offset, 1, eq.spaceJump.equipped)

    Srm.setBit(buf, 0xb9 + offset, 7, eq.screwAttack.collected)
    Srm.setBit(buf, 0x2 + offset, 3, eq.screwAttack.inInventory)
    Srm.setBit(buf, 0x0 + offset, 3, eq.screwAttack.equipped)

    Srm.setBit(buf, 0xb2 + offset, 7, eq.beams.charge.collected)
    Srm.setBit(buf, 0x7 + offset, 4, eq.beams.charge.inInventory)
    Srm.setBit(buf, 0x5 + offset, 4, eq.beams.charge.equipped)

    Srm.setBit(buf, 0xb6 + offset, 2, eq.beams.ice.collected)
    Srm.setBit(buf, 0x6 + offset, 1, eq.beams.ice.inInventory)
    Srm.setBit(buf, 0x4 + offset, 1, eq.beams.ice.equipped)

    Srm.setBit(buf, 0xb8 + offset, 4, eq.beams.wave.collected)
    Srm.setBit(buf, 0x6 + offset, 0, eq.beams.wave.inInventory)
    Srm.setBit(buf, 0x4 + offset, 0, eq.beams.wave.equipped)

    Srm.setBit(buf, 0xb5 + offset, 2, eq.beams.spazer.collected)
    Srm.setBit(buf, 0x6 + offset, 2, eq.beams.spazer.inInventory)
    Srm.setBit(buf, 0x4 + offset, 2, eq.beams.spazer.equipped)

    Srm.setBit(buf, 0xc1 + offset, 7, eq.beams.plasma.collected)
    Srm.setBit(buf, 0x6 + offset, 3, eq.beams.plasma.inInventory)
    Srm.setBit(buf, 0x4 + offset, 3, eq.beams.plasma.equipped)

    Srm.setBit(buf, 0xb7 + offset, 4, eq.grapple.collected)
    Srm.setBit(buf, 0x3 + offset, 6, eq.grapple.inInventory)
    Srm.setBit(buf, 0x1 + offset, 6, eq.grapple.equipped)

    Srm.setBit(buf, 0xb4 + offset, 6, eq.xRay.collected)
    Srm.setBit(buf, 0x3 + offset, 7, eq.xRay.inInventory)
    Srm.setBit(buf, 0x1 + offset, 7, eq.xRay.equipped)
  }

  private static writeResources(
    buf: Uint8Array,
    offset: number,
    res: Resources
  ): void {
    Srm.setWord(buf, 0x20 + offset, res.energy.current.dec)
    Srm.setWord(buf, 0x22 + offset, res.energy.max.dec)
    Srm.setWord(buf, 0x34 + offset, res.reserve.current.dec)
    Srm.setWord(buf, 0x32 + offset, res.reserve.max.dec)
    Srm.setWord(buf, 0x24 + offset, res.missiles.current.dec)
    Srm.setWord(buf, 0x26 + offset, res.missiles.max.dec)
    Srm.setWord(buf, 0x28 + offset, res.superMissiles.current.dec)
    Srm.setWord(buf, 0x2a + offset, res.superMissiles.max.dec)
    Srm.setWord(buf, 0x2c + offset, res.powerBombs.current.dec)
    Srm.setWord(buf, 0x2e + offset, res.powerBombs.max.dec)
  }

  private static writeCollectionItems(
    buf: Uint8Array,
    offset: number,
    items: CollectionItems
  ): void {
    const s = Srm.setBit
    const c = items.crateria
    s(buf, 0xb0 + offset, 1, c.missilePacks[0])
    s(buf, 0xb0 + offset, 2, c.missilePacks[1])
    s(buf, 0xb0 + offset, 3, c.missilePacks[2])
    s(buf, 0xb0 + offset, 4, c.missilePacks[3])
    s(buf, 0xb0 + offset, 6, c.missilePacks[4])
    s(buf, 0xb1 + offset, 1, c.missilePacks[5])
    s(buf, 0xb1 + offset, 2, c.missilePacks[6])
    s(buf, 0xb1 + offset, 4, c.missilePacks[7])
    s(buf, 0xb1 + offset, 3, c.superMissilePacks[0])
    s(buf, 0xb0 + offset, 0, c.powerBombPacks[0])
    s(buf, 0xb0 + offset, 5, c.energyTanks[0])

    const b = items.brinstar
    s(buf, 0xb1 + offset, 7, b.missilePacks[0])
    s(buf, 0xb2 + offset, 2, b.missilePacks[1])
    s(buf, 0xb2 + offset, 3, b.missilePacks[2])
    s(buf, 0xb2 + offset, 5, b.missilePacks[3])
    s(buf, 0xb2 + offset, 6, b.missilePacks[4])
    s(buf, 0xb3 + offset, 1, b.missilePacks[5])
    s(buf, 0xb3 + offset, 4, b.missilePacks[6])
    s(buf, 0xb4 + offset, 2, b.missilePacks[7])
    s(buf, 0xb4 + offset, 4, b.missilePacks[8])
    s(buf, 0xb4 + offset, 5, b.missilePacks[9])
    s(buf, 0xb5 + offset, 1, b.missilePacks[10])
    s(buf, 0xb5 + offset, 4, b.missilePacks[11])
    s(buf, 0xb1 + offset, 6, b.superMissilePacks[0])
    s(buf, 0xb2 + offset, 0, b.superMissilePacks[1])
    s(buf, 0xb3 + offset, 7, b.superMissilePacks[2])
    s(buf, 0xb1 + offset, 5, b.powerBombPacks[0])
    s(buf, 0xb3 + offset, 0, b.powerBombPacks[1])
    s(buf, 0xb3 + offset, 3, b.powerBombPacks[2])
    s(buf, 0xb4 + offset, 7, b.powerBombPacks[3])
    s(buf, 0xb5 + offset, 0, b.powerBombPacks[4])
    s(buf, 0xb3 + offset, 5, b.energyTanks[0])
    s(buf, 0xb3 + offset, 6, b.energyTanks[1])
    s(buf, 0xb4 + offset, 1, b.energyTanks[2])
    s(buf, 0xb4 + offset, 3, b.energyTanks[3])
    s(buf, 0xb5 + offset, 3, b.energyTanks[4])
    s(buf, 0xb2 + offset, 1, b.reserveTank)

    const n = items.norfair
    s(buf, 0xb6 + offset, 1, n.missilePacks[0])
    s(buf, 0xb6 + offset, 3, n.missilePacks[1])
    s(buf, 0xb6 + offset, 6, n.missilePacks[2])
    s(buf, 0xb6 + offset, 7, n.missilePacks[3])
    s(buf, 0xb7 + offset, 2, n.missilePacks[4])
    s(buf, 0xb7 + offset, 3, n.missilePacks[5])
    s(buf, 0xb7 + offset, 6, n.missilePacks[6])
    s(buf, 0xb7 + offset, 7, n.missilePacks[7])
    s(buf, 0xb8 + offset, 0, n.missilePacks[8])
    s(buf, 0xb8 + offset, 1, n.missilePacks[9])
    s(buf, 0xb8 + offset, 3, n.missilePacks[10])
    s(buf, 0xb8 + offset, 6, n.missilePacks[11])
    s(buf, 0xb9 + offset, 1, n.missilePacks[12])
    s(buf, 0xb9 + offset, 2, n.missilePacks[13])
    s(buf, 0xb9 + offset, 5, n.missilePacks[14])
    s(buf, 0xb8 + offset, 7, n.superMissilePacks[0])
    s(buf, 0xb7 + offset, 1, n.powerBombPacks[0])
    s(buf, 0xb9 + offset, 3, n.powerBombPacks[1])
    s(buf, 0xb9 + offset, 4, n.powerBombPacks[2])
    s(buf, 0xb6 + offset, 4, n.energyTanks[0])
    s(buf, 0xb7 + offset, 0, n.energyTanks[1])
    s(buf, 0xb9 + offset, 6, n.energyTanks[2])
    s(buf, 0xba + offset, 0, n.energyTanks[3])
    s(buf, 0xb7 + offset, 5, n.reserveTank)

    const w = items.wreckedShip
    s(buf, 0xc0 + offset, 0, w.missilePacks[0])
    s(buf, 0xc0 + offset, 2, w.missilePacks[1])
    s(buf, 0xc0 + offset, 3, w.missilePacks[2])
    s(buf, 0xc0 + offset, 5, w.superMissilePacks[0])
    s(buf, 0xc0 + offset, 6, w.superMissilePacks[1])
    s(buf, 0xc0 + offset, 4, w.energyTanks[0])
    s(buf, 0xc0 + offset, 1, w.reserveTank)

    const m = items.maridia
    s(buf, 0xc1 + offset, 0, m.missilePacks[0])
    s(buf, 0xc1 + offset, 3, m.missilePacks[1])
    s(buf, 0xc1 + offset, 5, m.missilePacks[2])
    s(buf, 0xc1 + offset, 6, m.missilePacks[3])
    s(buf, 0xc2 + offset, 0, m.missilePacks[4])
    s(buf, 0xc2 + offset, 2, m.missilePacks[5])
    s(buf, 0xc2 + offset, 4, m.missilePacks[6])
    s(buf, 0xc2 + offset, 7, m.missilePacks[7])
    s(buf, 0xc1 + offset, 1, m.superMissilePacks[0])
    s(buf, 0xc1 + offset, 4, m.superMissilePacks[1])
    s(buf, 0xc2 + offset, 5, m.superMissilePacks[2])
    s(buf, 0xc2 + offset, 3, m.powerBombPacks[0])
    s(buf, 0xc1 + offset, 2, m.energyTanks[0])
    s(buf, 0xc3 + offset, 0, m.energyTanks[1])
    s(buf, 0xc2 + offset, 1, m.reserveTank)
  }

  private static writeBosses(
    buf: Uint8Array,
    offset: number,
    bosses: Bosses
  ): void {
    Srm.setBit(buf, 0x68 + offset, 2, bosses.silverTorizo)
    Srm.setBit(buf, 0x69 + offset, 1, bosses.sporeSpawn)
    Srm.setBit(buf, 0x69 + offset, 0, bosses.kraid)
    Srm.setBit(buf, 0x6b + offset, 0, bosses.phantoon)
    Srm.setBit(buf, 0x6c + offset, 1, bosses.botwoon)
    Srm.setBit(buf, 0x6c + offset, 0, bosses.draygon)
    Srm.setBit(buf, 0x6a + offset, 1, bosses.crocomire)
    Srm.setBit(buf, 0x6a + offset, 2, bosses.goldenTorizo)
    Srm.setBit(buf, 0x6a + offset, 0, bosses.ridley)
  }

  private static writeStatues(
    buf: Uint8Array,
    offset: number,
    statues: Statues
  ): void {
    Srm.setBit(buf, 0x61 + offset, 1, statues.kraid)
    Srm.setBit(buf, 0x60 + offset, 6, statues.phantoon)
    Srm.setBit(buf, 0x61 + offset, 0, statues.draygon)
    Srm.setBit(buf, 0x60 + offset, 7, statues.ridley)
  }

  private static writeTime(
    buf: Uint8Array,
    offset: number,
    time: Game['time']
  ): void {
    Srm.setWord(buf, 0x3e + offset, time.hours.dec)
    Srm.setWord(buf, 0x3c + offset, time.minutes.dec)
  }

  private static writeMaps(
    buf: Uint8Array,
    offset: number,
    maps: Game['maps']
  ): void {
    Srm.setByte(buf, 0x148 + offset, maps.crateria ? 0xff : 0x00)
    Srm.setByte(buf, 0x149 + offset, maps.brinstar ? 0xff : 0x00)
    Srm.setByte(buf, 0x14a + offset, maps.norfair ? 0xff : 0x00)
    Srm.setByte(buf, 0x14b + offset, maps.wreckedShip ? 0xff : 0x00)
    Srm.setByte(buf, 0x14c + offset, maps.maridia ? 0xff : 0x00)
    Srm.setByte(buf, 0x14d + offset, maps.tourian ? 0xff : 0x00)
  }

  private static calcChecksums(buf: Uint8Array, gameOffset: number) {
    let high = 0
    let low = 0
    for (let index = 0; index < 0x65c; index++) {
      const current = buf[index + gameOffset]
      if (index % 2 === 0) {
        high += current
        if (high > 0xff) {
          high &= 0xff
          low++
        }
      } else {
        low += current
        if (low > 0xff) {
          low &= 0xff
        }
      }
    }
    return {
      checksum: { high, low },
      complement: { high: high ^ 0xff, low: low ^ 0xff }
    }
  }

  getGame(game: 'a' | 'b' | 'c'): Game {
    const offset = offsets.gameData[game]
    return {
      equipment: this.getEquipment(offset),
      resources: this.getResources(offset),
      collectionItems: this.getCollectionItems(offset),
      bosses: this.getBosses(offset),
      statues: this.getStatues(offset),
      time: {
        hours: this.getWord(0x3e + offset),
        minutes: this.getWord(0x3c + offset)
      },
      maps: {
        crateria: this.getByte(0x148 + offset).hex === 'ff',
        brinstar: this.getByte(0x149 + offset).hex === 'ff',
        norfair: this.getByte(0x14a + offset).hex === 'ff',
        wreckedShip: this.getByte(0x14b + offset).hex === 'ff',
        maridia: this.getByte(0x14c + offset).hex === 'ff',
        tourian: this.getByte(0x14d + offset).hex === 'ff'
      },
      checksums: this.getChecksums(offsets.checksums[game])
    }
  }

  getEquipment(offset: number): Equipments {
    return {
      morphBall: {
        collected: this.getBit(0xb3 + offset, 2),
        inInventory: this.getBit(0x2 + offset, 2),
        equipped: this.getBit(0x0 + offset, 2)
      },
      bombs: {
        collected: this.getBit(0xb0 + offset, 7),
        inInventory: this.getBit(0x3 + offset, 4),
        equipped: this.getBit(0x1 + offset, 4)
      },
      springBall: {
        collected: this.getBit(0xc2 + offset, 6),
        inInventory: this.getBit(0x2 + offset, 1),
        equipped: this.getBit(0x0 + offset, 1)
      },
      highJumpBoots: {
        collected: this.getBit(0xb6 + offset, 5),
        inInventory: this.getBit(0x3 + offset, 0),
        equipped: this.getBit(0x1 + offset, 0)
      },
      suits: {
        varia: {
          collected: this.getBit(0xb6 + offset, 0),
          inInventory: this.getBit(0x2 + offset, 0),
          equipped: this.getBit(0x0 + offset, 0)
        },
        gravity: {
          collected: this.getBit(0xc0 + offset, 7),
          inInventory: this.getBit(0x2 + offset, 5),
          equipped: this.getBit(0x0 + offset, 5)
        }
      },
      speedBooster: {
        collected: this.getBit(0xb8 + offset, 2),
        inInventory: this.getBit(0x3 + offset, 5),
        equipped: this.getBit(0x1 + offset, 5)
      },
      spaceJump: {
        collected: this.getBit(0xc3 + offset, 2),
        inInventory: this.getBit(0x3 + offset, 1),
        equipped: this.getBit(0x1 + offset, 1)
      },
      screwAttack: {
        collected: this.getBit(0xb9 + offset, 7),
        inInventory: this.getBit(0x2 + offset, 3),
        equipped: this.getBit(0x0 + offset, 3)
      },
      beams: {
        charge: {
          collected: this.getBit(0xb2 + offset, 7),
          inInventory: this.getBit(0x7 + offset, 4),
          equipped: this.getBit(0x5 + offset, 4)
        },
        ice: {
          collected: this.getBit(0xb6 + offset, 2),
          inInventory: this.getBit(0x6 + offset, 1),
          equipped: this.getBit(0x4 + offset, 1)
        },
        wave: {
          collected: this.getBit(0xb8 + offset, 4),
          inInventory: this.getBit(0x6 + offset, 0),
          equipped: this.getBit(0x4 + offset, 0)
        },
        spazer: {
          collected: this.getBit(0xb5 + offset, 2),
          inInventory: this.getBit(0x6 + offset, 2),
          equipped: this.getBit(0x4 + offset, 2)
        },
        plasma: {
          collected: this.getBit(0xc1 + offset, 7),
          inInventory: this.getBit(0x6 + offset, 3),
          equipped: this.getBit(0x4 + offset, 3)
        }
      },
      grapple: {
        collected: this.getBit(0xb7 + offset, 4),
        inInventory: this.getBit(0x3 + offset, 6),
        equipped: this.getBit(0x1 + offset, 6)
      },
      xRay: {
        collected: this.getBit(0xb4 + offset, 6),
        inInventory: this.getBit(0x3 + offset, 7),
        equipped: this.getBit(0x1 + offset, 7)
      }
    }
  }

  getResources(offset: number) {
    return {
      energy: {
        current: this.getWord(0x20 + offset),
        max: this.getWord(0x22 + offset)
      },
      reserve: {
        current: this.getWord(0x34 + offset),
        max: this.getWord(0x32 + offset)
      },
      missiles: {
        current: this.getWord(0x24 + offset),
        max: this.getWord(0x26 + offset)
      },
      superMissiles: {
        current: this.getWord(0x28 + offset),
        max: this.getWord(0x2a + offset)
      },
      powerBombs: {
        current: this.getWord(0x2c + offset),
        max: this.getWord(0x2e + offset)
      }
    }
  }

  getCollectionItems(offset: number) {
    return {
      crateria: {
        missilePacks: [
          this.getBit(0xb0 + offset, 1),
          this.getBit(0xb0 + offset, 2),
          this.getBit(0xb0 + offset, 3),
          this.getBit(0xb0 + offset, 4),
          this.getBit(0xb0 + offset, 6),
          this.getBit(0xb1 + offset, 1),
          this.getBit(0xb1 + offset, 2),
          this.getBit(0xb1 + offset, 4)
        ],
        superMissilePacks: [this.getBit(0xb1 + offset, 3)],
        powerBombPacks: [this.getBit(0xb0 + offset, 0)],
        energyTanks: [this.getBit(0xb0 + offset, 5)]
      },
      brinstar: {
        missilePacks: [
          this.getBit(0xb1 + offset, 7),
          this.getBit(0xb2 + offset, 2),
          this.getBit(0xb2 + offset, 3),
          this.getBit(0xb2 + offset, 5),
          this.getBit(0xb2 + offset, 6),
          this.getBit(0xb3 + offset, 1),
          this.getBit(0xb3 + offset, 4),
          this.getBit(0xb4 + offset, 2),
          this.getBit(0xb4 + offset, 4),
          this.getBit(0xb4 + offset, 5),
          this.getBit(0xb5 + offset, 1),
          this.getBit(0xb5 + offset, 4)
        ],
        superMissilePacks: [
          this.getBit(0xb1 + offset, 6),
          this.getBit(0xb2 + offset, 0),
          this.getBit(0xb3 + offset, 7)
        ],
        powerBombPacks: [
          this.getBit(0xb1 + offset, 5),
          this.getBit(0xb3 + offset, 0),
          this.getBit(0xb3 + offset, 3),
          this.getBit(0xb4 + offset, 7),
          this.getBit(0xb5 + offset, 0)
        ],
        energyTanks: [
          this.getBit(0xb3 + offset, 5),
          this.getBit(0xb3 + offset, 6),
          this.getBit(0xb4 + offset, 1),
          this.getBit(0xb4 + offset, 3),
          this.getBit(0xb5 + offset, 3)
        ],
        reserveTank: this.getBit(0xb2 + offset, 1)
      },
      norfair: {
        missilePacks: [
          this.getBit(0xb6 + offset, 1),
          this.getBit(0xb6 + offset, 3),
          this.getBit(0xb6 + offset, 6),
          this.getBit(0xb6 + offset, 7),
          this.getBit(0xb7 + offset, 2),
          this.getBit(0xb7 + offset, 3),
          this.getBit(0xb7 + offset, 6),
          this.getBit(0xb7 + offset, 7),
          this.getBit(0xb8 + offset, 0),
          this.getBit(0xb8 + offset, 1),
          this.getBit(0xb8 + offset, 3),
          this.getBit(0xb8 + offset, 6),
          this.getBit(0xb9 + offset, 1),
          this.getBit(0xb9 + offset, 2),
          this.getBit(0xb9 + offset, 5)
        ],
        superMissilePacks: [this.getBit(0xb8 + offset, 7)],
        powerBombPacks: [
          this.getBit(0xb7 + offset, 1),
          this.getBit(0xb9 + offset, 3),
          this.getBit(0xb9 + offset, 4)
        ],
        energyTanks: [
          this.getBit(0xb6 + offset, 4),
          this.getBit(0xb7 + offset, 0),
          this.getBit(0xb9 + offset, 6),
          this.getBit(0xba + offset, 0)
        ],
        reserveTank: this.getBit(0xb7 + offset, 5)
      },
      wreckedShip: {
        missilePacks: [
          this.getBit(0xc0 + offset, 0),
          this.getBit(0xc0 + offset, 2),
          this.getBit(0xc0 + offset, 3)
        ],
        superMissilePacks: [
          this.getBit(0xc0 + offset, 5),
          this.getBit(0xc0 + offset, 6)
        ],
        energyTanks: [this.getBit(0xc0 + offset, 4)],
        reserveTank: this.getBit(0xc0 + offset, 1)
      },
      maridia: {
        missilePacks: [
          this.getBit(0xc1 + offset, 0),
          this.getBit(0xc1 + offset, 3),
          this.getBit(0xc1 + offset, 5),
          this.getBit(0xc1 + offset, 6),
          this.getBit(0xc2 + offset, 0),
          this.getBit(0xc2 + offset, 2),
          this.getBit(0xc2 + offset, 4),
          this.getBit(0xc2 + offset, 7)
        ],
        superMissilePacks: [
          this.getBit(0xc1 + offset, 1),
          this.getBit(0xc1 + offset, 4),
          this.getBit(0xc2 + offset, 5)
        ],
        powerBombPacks: [this.getBit(0xc2 + offset, 3)],
        energyTanks: [
          this.getBit(0xc1 + offset, 2),
          this.getBit(0xc3 + offset, 0)
        ],
        reserveTank: this.getBit(0xc2 + offset, 1)
      }
    }
  }

  getBosses(offset: number): Bosses {
    return {
      silverTorizo: this.getBit(0x68 + offset, 2),
      sporeSpawn: this.getBit(0x69 + offset, 1),
      kraid: this.getBit(0x69 + offset, 0),
      phantoon: this.getBit(0x6b + offset, 0),
      botwoon: this.getBit(0x6c + offset, 1),
      draygon: this.getBit(0x6c + offset, 0),
      crocomire: this.getBit(0x6a + offset, 1),
      goldenTorizo: this.getBit(0x6a + offset, 2),
      ridley: this.getBit(0x6a + offset, 0)
    }
  }

  getStatues(offset: number): Statues {
    return {
      kraid: this.getBit(0x61 + offset, 1),
      phantoon: this.getBit(0x60 + offset, 6),
      draygon: this.getBit(0x61 + offset, 0),
      ridley: this.getBit(0x60 + offset, 7)
    }
  }

  getChecksums(offset: number) {
    return {
      checksum: {
        high: this.getByte(0x0 + offset),
        low: this.getByte(0x1 + offset)
      },
      complement: {
        high: this.getByte(0x8 + offset),
        low: this.getByte(0x9 + offset)
      },
      checksumCopy: {
        high: this.getByte(0x1ff0 + offset),
        low: this.getByte(0x1ff1 + offset)
      },
      complementCopy: {
        high: this.getByte(0x1ff8 + offset),
        low: this.getByte(0x1ff9 + offset)
      }
    }
  }

  calculateChecksums(game: 'a' | 'b' | 'c') {
    return Srm.calcChecksums(this.srm, offsets.gameData[game])
  }

  getWord(offset: number) {
    const hexWordString = getHexWordString(this.srm, offset)
    return { hex: hexWordString, dec: parseInt(hexWordString, 16) }
  }

  getByte(offset: number) {
    const hexByteString = getHexByteString(this.srm, offset)
    return { hex: hexByteString, dec: parseInt(hexByteString, 16) }
  }

  getBit(offset: number, bitPosition: number) {
    const b = getHexByteString(this.srm, offset)
    return parseInt(b, 16).toString(2).padStart(8, '0')[7 - bitPosition] === '1'
  }
}

const getHexByteString = (str: Buffer, offset: number) => {
  const string = str[offset]?.toString(16).padStart(2, '0')
  if (!string) {
    throw new Error(`unexpected null string at offset ${offset}`)
  }

  if (string.length !== 2) {
    throw new Error(
      `unexpected string length ${string.length} at offset ${offset}; str: ${str}; string: ${string}`
    )
  }
  return string
}

const getHexWordString = (str: Buffer, offset: number) => {
  const first = getHexByteString(str, offset + 1)
  const second = getHexByteString(str, offset)
  return `${first}${second}`
}
